import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { LocationDataStatus } from 'ui/enums';
import { LocationData } from 'ui/types/interfaces/activity-forms';

import { ICreateActivityDTO, IDatesDTO } from '~/types/dtos';

import { MultipleLocationsOngoingProps } from './types';
import { formInitialDefaultValues, schemaValidation } from './utils';

export const useMultipleLocationsOngoingController = ({
  handleContinue,
  handleBack,
  isLoading,
  defaultValues,
  defaultLocationGroupsByDate,
  isEditing,
}: MultipleLocationsOngoingProps) => {
  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    setValue,
  } = useForm<ICreateActivityDTO>({
    resolver: yupResolver(schemaValidation),
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: defaultValues || formInitialDefaultValues,
  });

  const [locationsByGroup, setLocationsByGroup] = useState<
    Record<number, LocationData[]> | undefined
  >(defaultLocationGroupsByDate);
  const {
    fields: locationsGroupsFields,
    append: appendLocationGroup,
    remove: removeLocationGroup,
    update: updateLocationGroupsFields,
  } = useFieldArray<ICreateActivityDTO>({
    control,
    name: 'locationsGroups',
  });

  const handleSubmitForm = handleSubmit((data: ICreateActivityDTO) => {
    handleContinue({ ...data, locationsByGroup });
  });

  const verifyIfExistsNotFoundLocation = useCallback(() => {
    if (!locationsByGroup) return false;

    const allLocations = Object.values(locationsByGroup).flat();

    const existsNotFoundLocation = allLocations.find(
      (location) => location.status === LocationDataStatus.ZERO_RESULTS,
    );

    if (existsNotFoundLocation) {
      return true;
    }

    return false;
  }, [locationsByGroup]);

  const verifyIfAllLocationsAreNotFound = useCallback(() => {
    if (!locationsByGroup) return true;

    const allLocations = Object.values(locationsByGroup).flat();

    const isEveryLocationNotFound = allLocations.every(
      (location) => location.status === LocationDataStatus.ZERO_RESULTS,
    );

    return isEveryLocationNotFound;
  }, [locationsByGroup]);

  const verifyIfAllGroupsAreNotEmpty = useCallback(() => {
    if (!locationsByGroup) return true;

    const groupIndexes = Object.keys(locationsByGroup);

    let isAllGroupsNotEmpty = false;
    groupIndexes.every((groupIndex) => {
      isAllGroupsNotEmpty = locationsByGroup[Number(groupIndex)].length > 0;
      return isAllGroupsNotEmpty;
    });

    return isAllGroupsNotEmpty;
  }, [locationsByGroup]);

  const isDisabledPublishButton =
    verifyIfAllLocationsAreNotFound() ||
    verifyIfExistsNotFoundLocation() ||
    !verifyIfAllGroupsAreNotEmpty() ||
    !isValid;

  const addLocationGroup = useCallback(
    (groupIndex: number) => {
      appendLocationGroup({
        noVolunteerLimit: false,
      } as IDatesDTO);
    },
    [appendLocationGroup],
  );

  const deleteLocationGroup = useCallback(
    (groupIndex: number) => {
      removeLocationGroup(groupIndex);
      if (locationsByGroup) {
        const newLocationsByGroup = locationsByGroup;

        delete newLocationsByGroup[groupIndex];

        const keys = Object.keys(newLocationsByGroup);

        const orderedNewLocationsByGroup = keys.reduce(
          (
            accValue: Record<number, LocationData[]>,
            currValue: string,
            currIndex,
          ) => {
            const locationData = newLocationsByGroup[Number(currValue)];
            return { ...accValue, [currIndex]: locationData };
          },

          {} as Record<number, LocationData[]>,
        );
        setLocationsByGroup(orderedNewLocationsByGroup);
      }
    },
    [removeLocationGroup, locationsByGroup],
  );

  const handleUpdateLocationGroup = (
    index: number,
    locations: LocationData[],
  ) => {
    setLocationsByGroup((prevState) => {
      const locationGroups = prevState;
      return { ...locationGroups, [index]: locations };
    });
  };

  const handleDeleteAllLocationsFromGroup = (index: number) => {
    setLocationsByGroup((prevState) => {
      const locationGroups = prevState;
      if (locationGroups && locationGroups[index]) {
        const updatedLocationGroup: Record<number, LocationData[]> = {};

        for (const [groupIndex] of Object.values(locationGroups).entries()) {
          if (groupIndex !== index) {
            updatedLocationGroup[groupIndex] = locationGroups[groupIndex];
          }
        }
        return updatedLocationGroup;
      }
    });
  };

  const handleDeleteLocationFromGroup = (
    groupIndex: number,
    locationIndexToRemove: number,
  ) => {
    setLocationsByGroup((prevState) => {
      const locationGroups = prevState;
      if (locationGroups && locationGroups[groupIndex]) {
        const locationGroup = locationGroups[groupIndex];
        const newLocations = locationGroup.filter(
          (location, index) => locationIndexToRemove !== index,
        );
        return { ...locationGroups, [groupIndex]: newLocations };
      }

      return prevState;
    });
  };
  const updateNoVolunteerLimitValue = (value: boolean, index: number) => {
    updateLocationGroupsFields(index, { noVolunteerLimit: value });
  };
  return {
    locationsGroupsFields,
    appendLocationGroup,
    addLocationGroup,
    deleteLocationGroup,
    handleBack,
    handleSubmitForm,
    control,
    isLoading,
    isDisabledPublishButton,
    errors,
    locationsByGroup,
    handleUpdateLocationGroup,
    handleDeleteAllLocationsFromGroup,
    handleDeleteLocationFromGroup,
    updateNoVolunteerLimitValue,
    setValue,
    isEditing,
  };
};
